import { supabase } from './supabase';
import type { Session } from '@supabase/supabase-js';

/**
 * Authentication and Ownership Helper Functions
 * 
 * These functions provide centralized logic for determining user authentication
 * status, user types, and camp ownership for the claim button flow.
 */

export interface UserAuthStatus {
  isAuthenticated: boolean;
  userId: string | null;
  userTypes: string[];
  isCampOwner: boolean;
}

export interface CampOwnershipStatus {
  isOwner: boolean;
  isClaimed: boolean;
  ownerId: string | null;
  planId: string | null;
  claimedAt: string | null;
  hasValidPlan: boolean;
}

/**
 * Get comprehensive authentication status for the current user
 */
export async function getUserAuthStatus(session: Session | null): Promise<UserAuthStatus> {
  if (!session?.user) {
    return {
      isAuthenticated: false,
      userId: null,
      userTypes: [],
      isCampOwner: false,
    };
  }

  try {
    // Get user types from user_types table (source of truth)
    const { data: userTypeData, error } = await supabase
      .from('user_types')
      .select('type')
      .eq('user_id', session.user.id)
      .single();

    let userTypes: string[] = ['parent']; // Default to parent
    
    if (error) {
      // If no row found or query failed, fallback to metadata for backward compatibility
      if (error.code !== 'PGRST116') {
        console.warn('Error fetching user types from table, falling back to metadata:', error);
      }
      userTypes = session.user.raw_user_meta_data?.user_types || 
                  session.user.user_metadata?.user_types || 
                  ['parent'];
    } else {
      userTypes = userTypeData?.type || ['parent'];
    }

    const isCampOwner = userTypes.includes('camp-owner');

    return {
      isAuthenticated: true,
      userId: session.user.id,
      userTypes: userTypes,
      isCampOwner,
    };
  } catch (error) {
    console.error('Error in getUserAuthStatus:', error);
    // Fallback to metadata if table query fails
    const fallbackTypes = session.user.raw_user_meta_data?.user_types || 
                         session.user.user_metadata?.user_types || 
                         ['parent'];
    return {
      isAuthenticated: true,
      userId: session.user.id,
      userTypes: fallbackTypes,
      isCampOwner: fallbackTypes.includes('camp-owner'),
    };
  }
}

/**
 * Get ownership status for a specific camp
 */
export async function getCampOwnershipStatus(campId: string, userId: string | null): Promise<CampOwnershipStatus> {
  try {
    const { data: camp, error } = await supabase
      .from('camps')
      .select('claimed, owner_id, plan_id, claimed_at')
      .eq('id', campId)
      .single();

    if (error) {
      console.error('Error fetching camp ownership:', error);
      return {
        isOwner: false,
        isClaimed: false,
        ownerId: null,
        planId: null,
        claimedAt: null,
        hasValidPlan: false,
      };
    }

    const isOwner = userId ? camp.owner_id === userId : false;
    const hasValidPlan = camp.plan_id && ['trailhead', 'campfire', 'summit', 'everest'].includes(camp.plan_id);

    return {
      isOwner,
      isClaimed: camp.claimed,
      ownerId: camp.owner_id,
      planId: camp.plan_id,
      claimedAt: camp.claimed_at,
      hasValidPlan: !!hasValidPlan,
    };
  } catch (error) {
    console.error('Error in getCampOwnershipStatus:', error);
    return {
      isOwner: false,
      isClaimed: false,
      ownerId: null,
      planId: null,
      claimedAt: null,
      hasValidPlan: false,
    };
  }
}

/**
 * Check if user already has a claimed camp
 */
export async function hasClaimedCamp(userId: string | null): Promise<boolean> {
  if (!userId) return false;
  
  try {
    const { data, error } = await supabase
      .from('camps')
      .select('id')
      .eq('owner_id', userId)
      .eq('claimed', true)
      .limit(1);
    
    if (error) {
      console.error('Error checking claimed camps:', error);
      return false;
    }
    
    return (data?.length || 0) > 0;
  } catch (error) {
    console.error('Error in hasClaimedCamp:', error);
    return false;
  }
}

/**
 * Determine the appropriate action for the claim button based on user and camp status
 */
export interface ClaimButtonAction {
  action: 'redirect-to-signup' | 'redirect-to-pricing' | 'show-dashboard' | 'show-claimed-message' | 'hide';
  redirectUrl?: string;
  message?: string;
}

export async function determineClaimButtonAction(
  session: Session | null,
  campId: string
): Promise<ClaimButtonAction> {
  const userAuth = await getUserAuthStatus(session);
  const campOwnership = await getCampOwnershipStatus(campId, userAuth.userId);

  // FIRST: Check if camp is claimed - this takes priority over everything else
  // If camp is claimed by someone else, show claimed message (regardless of auth status)
  if (campOwnership.isClaimed && !campOwnership.isOwner) {
    return {
      action: 'show-claimed-message',
      message: 'This camp has been claimed by its owner.',
    };
  }

  // If user is the owner and camp is claimed, show dashboard (plan is optional - only needed for editing)
  if (campOwnership.isOwner && campOwnership.isClaimed) {
    return {
      action: 'show-dashboard',
    };
  }

  // If camp is claimed but we couldn't determine owner (edge case), show claimed message
  if (campOwnership.isClaimed) {
    return {
      action: 'show-claimed-message',
      message: 'This camp has been claimed by its owner.',
    };
  }

  // If not authenticated, redirect to sign-up with business type preselected
  if (!userAuth.isAuthenticated) {
    return {
      action: 'redirect-to-signup',
      redirectUrl: '/login?type=business&redirect=/mycamp-teaser',
    };
  }

  // If user is the owner but camp is not claimed, redirect to pricing
  if (campOwnership.isOwner && !campOwnership.isClaimed) {
    return {
      action: 'redirect-to-pricing',
      redirectUrl: '/pricing/owners',
    };
  }

  // If user is authenticated but not a camp owner, redirect to pricing
  if (userAuth.isAuthenticated && !userAuth.isCampOwner) {
    return {
      action: 'redirect-to-pricing',
      redirectUrl: '/pricing/owners',
    };
  }

  // Default: show claim button (camp is not claimed and user is camp owner)
  return {
    action: 'redirect-to-pricing',
    redirectUrl: '/pricing/owners',
  };
}

/**
 * Check if user has the required user type for camp ownership
 */
export function hasRequiredUserType(userTypes: string[], requiredType: string = 'camp-owner'): boolean {
  return userTypes.includes(requiredType);
}

/**
 * Validate that a plan ID is valid
 */
export function isValidPlanId(planId: string | null): boolean {
  if (!planId) return false;
  return ['trailhead', 'campfire', 'summit', 'everest'].includes(planId);
}

